import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Avatar,
  Divider,
  Chip,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Icon } from "@iconify/react";
import closeFill from "@iconify/icons-eva/close-fill";
import emailFill from "@iconify/icons-eva/email-fill";
import phoneFill from "@iconify/icons-eva/phone-fill";
import pinFill from "@iconify/icons-eva/pin-fill";
import briefcaseFill from "@iconify/icons-eva/briefcase-fill";
import personFill from "@iconify/icons-eva/person-fill";
import clockFill from "@iconify/icons-eva/clock-fill";
import { getDelegateDetail } from "../../DAL/Delegate";
import { s3baseUrl } from "../../config/config";
import Label from "../../components/Label";

export default function DelegateDetails({ open, onClose, delegateId }) {
  const [delegate, setDelegate] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && delegateId) {
      fetchDelegateDetail();
    }
  }, [open, delegateId]);

  const fetchDelegateDetail = async () => {
    setLoading(true);
    try {
      const response = await getDelegateDetail(delegateId);
      console.log("Coach detail response:", response);
      if (response.code === 200) {
        setDelegate(response.coach);
      }
    } catch (error) {
      console.error("Error fetching Coach details:", error);
    } finally {
      setLoading(false);
    }
  };

  const DetailItem = ({ label, value, icon }) => (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        {icon && (
          <Icon
            icon={icon}
            width={16}
            height={16}
            style={{ color: "var(--mui-palette-text-secondary)" }}
          />
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        variant="body1"
        color="text.primary"
        sx={{
          pl: icon ? 3 : 0,
          fontWeight: 500,
          lineHeight: 1.5,
        }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        component="div"
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Coach Details</Typography>
        <IconButton onClick={onClose}>
          <Icon icon={closeFill} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress />
          </Box>
        ) : delegate ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              minHeight: 400,
            }}
          >
            {/* Left Side: Profile Summary */}
            <Box
              sx={{
                width: { xs: "100%", md: 300 },
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                borderRight: (theme) => ({
                  md: `1px solid ${theme.palette.divider}`,
                }),
              }}
            >
              <Box sx={{ position: "relative", mb: 3 }}>
                <Avatar
                  src={
                    delegate.image?.thumbnail_1
                      ? s3baseUrl + delegate.image.thumbnail_1
                      : undefined
                  }
                  alt={`${delegate.first_name} ${delegate.last_name}`}
                  sx={{
                    width: 120,
                    height: 120,
                    border: "4px solid #fff",
                    boxShadow: (theme) => theme.customShadows.z8,
                  }}
                >
                  {delegate.first_name?.charAt(0)}
                </Avatar>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 8,
                    right: 8,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    bgcolor:
                      delegate.status === true ? "success.main" : "error.main",
                    border: "3px solid #fff",
                  }}
                />
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {`${delegate.first_name} ${delegate.last_name}`}
              </Typography>

              <Label
                variant="filled"
                color={delegate.status === true ? "success" : "error"}
                sx={{ mb: 3, textTransform: "uppercase", fontWeight: 700 }}
              >
                {delegate.status === true ? "Active" : "Inactive"}
              </Label>

              <Stack spacing={2} sx={{ width: "100%" }}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ color: "text.secondary" }}
                >
                  <Icon icon={emailFill} width={20} />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, wordBreak: "break-all" }}
                  >
                    {delegate.email}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ color: "text.secondary" }}
                >
                  <Icon icon={phoneFill} width={20} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {delegate.contact_number || "N/A"}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            {/* Right Side: Detailed Information */}
            <Box sx={{ flex: 1, p: 4, overflowY: "auto", maxHeight: 600 }}>
              <Stack spacing={4}>
                {/* Location Section */}
                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2.5 }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                      }}
                    >
                      <Icon icon={pinFill} width={20} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Location Details
                    </Typography>
                  </Stack>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <DetailItem label="Address" value={delegate.address} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="City" value={delegate.city} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="State" value={delegate.state} />
                    </Grid>
                    <Grid item xs={12}>
                      <DetailItem
                        label="Time Zone"
                        value={delegate.time_zone}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Professional Section */}
                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2.5 }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                      }}
                    >
                      <Icon icon={briefcaseFill} width={20} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Professional Profile
                    </Typography>
                  </Stack>

                  <DetailItem
                    label="Main Heading"
                    value={delegate.main_heading}
                  />
                  <DetailItem label="Biography" value={delegate.biography} />
                </Box>
              </Stack>
            </Box>
          </Box>
        ) : (
          <Box sx={{ py: 10, textAlign: "center" }}>
            <Icon
              icon={personFill}
              width={48}
              sx={{ color: "text.disabled", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary">
              No Coach information found.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
